import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Stack,
  TextField,
  IconButton,
  CircularProgress,
  Typography,
  Avatar,
  Fade,
  keyframes,
} from "@mui/material";
import { useMediaQuery, useTheme } from "@mui/material";

import SendIcon from "@mui/icons-material/Send";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { ENDPOINT } from "../configuration/configuration";

/* ================= CONSTANTS ================= */
const SESSION_KEY = "voiceqa_session_id";

const GREETING_TEXT =
  "Namaste, I'm Swastika your personal health assistant. You can ask me anything related to emergency and health-related inquiries.";

const greetingImageSource = require("../assets/images/swastika.png");

const COLORS = {
  brand: "#1a237e",
  brandMid: "#343275",
  brandLight: "#5C5FBD",
  brandSoft: "#EEF0FA",
  brandGradient: "linear-gradient(135deg, #1a237e 0%, #343275 55%, #5C5FBD 100%)",
  userBubble: "#1a237e",
  botBubble: "#FFFFFF",
  pageBg: "linear-gradient(160deg, #EEF0FA 0%, #E3E6F5 45%, #D5DAF0 100%)",
  muted: "#6B7A8A",
  border: "rgba(26, 35, 126, 0.1)",
};

const pulse = keyframes`
  0%, 100% { opacity: 0.35; transform: scale(0.9); }
  50% { opacity: 1; transform: scale(1); }
`;

const recordPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.45); }
  70% { box-shadow: 0 0 0 10px rgba(211, 47, 47, 0); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* ================= SESSION ================= */
const getSessionId = () => {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
};

/* ================= SAFE FETCH ================= */
const safeFetchJSON = async (url, options) => {
  const res = await fetch(url, options);
  const ct = res.headers.get("content-type") || "";
  if (!res.ok || !ct.includes("application/json")) {
    throw new Error("Invalid response");
  }
  return res.json();
};

/* ================= COMPONENT ================= */
const AIChatScreen = () => {
  const sessionIdRef = useRef(getSessionId());
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const [recording, setRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const audioPlayerRef = useRef(null);
  const [playingId, setPlayingId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const bottomRef = useRef(null);
  const messagesRef = useRef(null);
  const [viewportHeight, setViewportHeight] = useState(
    () => window.visualViewport?.height || window.innerHeight
  );
  const [viewportOffset, setViewportOffset] = useState(0);

  /* ================= INIT ================= */
  useEffect(() => {
    setMessages([
      {
        id: "greet",
        sender: "bot",
        text: GREETING_TEXT,
        image: greetingImageSource,
      },
    ]);
  }, []);

  // Keep chat height inside the visible area when mobile keyboard opens
  useEffect(() => {
    const updateHeight = () => {
      const next = window.visualViewport?.height || window.innerHeight;
      const offset = window.visualViewport?.offsetTop || 0;
      setViewportHeight(next);
      setViewportOffset(offset);
    };

    updateHeight();
    window.visualViewport?.addEventListener("resize", updateHeight);
    window.visualViewport?.addEventListener("scroll", updateHeight);
    window.addEventListener("resize", updateHeight);

    const prevOverflow = document.body.style.overflow;
    if (isMobile) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      window.visualViewport?.removeEventListener("resize", updateHeight);
      window.visualViewport?.removeEventListener("scroll", updateHeight);
      window.removeEventListener("resize", updateHeight);
      document.body.style.overflow = prevOverflow;
    };
  }, [isMobile]);

  useEffect(() => {
    // Scroll only inside the messages panel — avoids pushing header off-screen
    const panel = messagesRef.current;
    if (panel) {
      panel.scrollTop = panel.scrollHeight;
    }
  }, [messages, loading]);

  /* ================= SESSION SYNC ================= */
  const syncSession = (data) => {
    if (data?.session_id && data.session_id !== sessionIdRef.current) {
      sessionIdRef.current = data.session_id;
      localStorage.setItem(SESSION_KEY, data.session_id);
    }
  };

  /* ================= AUDIO ================= */
  const playAudio = (src, id) => {
    if (audioPlayerRef.current) audioPlayerRef.current.pause();

    const audio = new Audio(src);
    audioPlayerRef.current = audio;
    setPlayingId(id);
    setIsPlaying(true);

    audio.play().catch(() => {});
    audio.onended = () => {
      setPlayingId(null);
      setIsPlaying(false);
    };
  };

  const toggleAudio = (msg) => {
    if (!msg.audio || !msg.fromVoice) return;

    if (playingId === msg.id && isPlaying) {
      audioPlayerRef.current.pause();
      setIsPlaying(false);
    } else {
      playAudio(msg.audio, msg.id);
    }
  };

  /* ================= TEXT CHAT ================= */
  const sendText = async () => {
    if (!input.trim() || loading) return;

    const text = input.trim();
    setInput("");

    setMessages((p) => [...p, { id: Date.now(), sender: "user", text }]);
    setLoading(true);

    try {
      const fd = new FormData();
      fd.append("text", text);
      fd.append("session_id", sessionIdRef.current);

      const data = await safeFetchJSON(ENDPOINT.voiceAssistant, {
        method: "POST",
        body: fd,
      });

      syncSession(data);

      setMessages((p) => [
        ...p,
        {
          id: Date.now() + 1,
          sender: "bot",
          text: data.answer,
          fromVoice: false,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  /* ================= VOICE CHAT ================= */
  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mr = new MediaRecorder(stream);

    mediaRecorderRef.current = mr;
    audioChunksRef.current = [];

    mr.ondataavailable = (e) =>
      e.data.size && audioChunksRef.current.push(e.data);

    mr.start();
    setRecording(true);
  };

  const stopRecording = () => {
    mediaRecorderRef.current.stop();
    setRecording(false);

    mediaRecorderRef.current.onstop = async () => {
      const blob = new Blob(audioChunksRef.current, { type: "audio/wav" });

      const fd = new FormData();
      fd.append("file", blob);
      fd.append("session_id", sessionIdRef.current);

      setLoading(true);

      try {
        const data = await safeFetchJSON(ENDPOINT.voiceAssistant, {
          method: "POST",
          body: fd,
        });

        syncSession(data);

        const audioSrc = data.audio_base64
          ? `data:audio/mp3;base64,${data.audio_base64}`
          : null;

        const botId = Date.now() + 1;

        setMessages((p) => [
          ...p,
          { id: Date.now(), sender: "user", text: data.transcript },
          {
            id: botId,
            sender: "bot",
            text: data.answer,
            audio: audioSrc,
            fromVoice: true,
          },
        ]);

        if (audioSrc) playAudio(audioSrc, botId);
      } finally {
        setLoading(false);
      }
    };
  };

  /* ================= UI ================= */
  return (
    <Box
      sx={{
        height: isMobile ? `${viewportHeight}px` : "100vh",
        maxHeight: isMobile ? `${viewportHeight}px` : "100vh",
        width: "100%",
        maxWidth: "100vw",
        display: "flex",
        justifyContent: "center",
        alignItems: isMobile ? "stretch" : "center",
        background: COLORS.pageBg,
        p: isMobile ? 0 : 2,
        overflow: "hidden",
        position: isMobile ? "fixed" : "relative",
        top: isMobile ? `${viewportOffset}px` : "auto",
        left: isMobile ? 0 : "auto",
        right: isMobile ? 0 : "auto",
        zIndex: isMobile ? 1300 : "auto",
      }}
    >
      <Stack
        sx={{
          width: isMobile ? "100%" : 440,
          height: "100%",
          maxHeight: isMobile ? "100%" : "min(92vh, 780px)",
          background: "#FAFCFD",
          borderRadius: isMobile ? 0 : 3,
          boxShadow: isMobile
            ? "none"
            : "0 20px 50px rgba(27, 42, 74, 0.12)",
          overflow: "hidden",
          border: isMobile ? "none" : `1px solid ${COLORS.border}`,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            pt: 2,
            pb: 1.75,
            background: COLORS.brandGradient,
            color: "#fff",
            overflow: "visible",
            flexShrink: 0,
          }}
        >
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ overflow: "visible" }}
          >
            <IconButton
              onClick={() => navigate(-1)}
              aria-label="Go back"
              sx={{
                color: "#fff",
                p: 0.75,
                mr: 0.25,
                flexShrink: 0,
                "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Avatar
              src={greetingImageSource}
              alt="Swastika"
              sx={{
                width: 48,
                height: 48,
                flexShrink: 0,
                bgcolor: COLORS.brandSoft,
                border: "2px solid rgba(255,255,255,0.45)",
                "& img": {
                  objectFit: "cover",
                  objectPosition: "top center",
                },
              }}
            />
            <Box sx={{ flex: 1, textAlign: "left", minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 17,
                  letterSpacing: 0.2,
                  lineHeight: 1.2,
                }}
              >
                Swastika
              </Typography>
              <Stack direction="row" spacing={0.75} alignItems="center" mt={0.25}>
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    bgcolor: "#4ADE80",
                    animation: `${pulse} 2s ease-in-out infinite`,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 12,
                    opacity: 0.85,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  Health assistant · Online
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </Box>

        {/* Notice */}
        <Box
          sx={{
            mx: 2,
            mt: 1.5,
            mb: 0.5,
            px: 1.5,
            py: 1,
            background: COLORS.brandGradient,
            border: "none",
            borderRadius: 2,
            textAlign: "left",
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: 12.5,
              color: "#fff",
              lineHeight: 1.45,
            }}
          >
            Swastika assists only with emergency and health-related inquiries.
          </Typography>
        </Box>

        {/* Messages */}
        <Stack
          ref={messagesRef}
          sx={{
            flex: 1,
            minHeight: 0,
            px: 2,
            py: 1.5,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            gap: 0.5,
            background:
              "radial-gradient(ellipse at top, rgba(26,35,126,0.05), transparent 60%)",
          }}
        >
          {messages.map((m) => {
            const isUser = m.sender === "user";
            return (
              <Stack
                key={m.id}
                direction="row"
                spacing={1}
                sx={{
                  justifyContent: isUser ? "flex-end" : "flex-start",
                  alignItems: "flex-end",
                  mb: 1.25,
                  animation: `${slideUp} 0.35s ease-out`,
                }}
              >
                {!isUser && (
                  <Avatar
                    src={m.image || greetingImageSource}
                    alt="Swastika"
                    sx={{
                      width: 32,
                      height: 32,
                      mb: 0.25,
                      bgcolor: COLORS.brandSoft,
                      "& img": {
                        objectFit: "cover",
                        objectPosition: "top center",
                      },
                    }}
                  />
                )}

                <Box
                  sx={{
                    bgcolor: isUser ? COLORS.userBubble : COLORS.botBubble,
                    color: isUser ? "#fff" : COLORS.brand,
                    borderRadius: isUser
                      ? "18px 18px 4px 18px"
                      : "18px 18px 18px 4px",
                    px: 1.75,
                    py: 1.25,
                    maxWidth: "78%",
                    boxShadow: isUser
                      ? "0 4px 14px rgba(26, 35, 126, 0.2)"
                      : "0 2px 10px rgba(26, 35, 126, 0.06)",
                    border: isUser ? "none" : `1px solid ${COLORS.border}`,
                    textAlign: "left",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 14,
                      lineHeight: 1.5,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {m.text}
                  </Typography>

                  {m.sender === "bot" && m.fromVoice && m.audio && (
                    <IconButton
                      size="small"
                      onClick={() => toggleAudio(m)}
                      sx={{
                        mt: 0.5,
                        ml: -0.5,
                        color: COLORS.brand,
                        bgcolor: COLORS.brandSoft,
                        "&:hover": { bgcolor: "#E0E3F5" },
                      }}
                    >
                      {playingId === m.id && isPlaying ? (
                        <PauseIcon fontSize="small" />
                      ) : (
                        <PlayArrowIcon fontSize="small" />
                      )}
                    </IconButton>
                  )}
                </Box>
              </Stack>
            );
          })}

          {loading && (
            <Fade in>
              <Stack
                direction="row"
                spacing={1}
                alignItems="flex-end"
                sx={{ mb: 1.25 }}
              >
                <Avatar
                  src={greetingImageSource}
                  alt="Swastika"
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: COLORS.brandSoft,
                    "& img": {
                      objectFit: "cover",
                      objectPosition: "top center",
                    },
                  }}
                />
                <Box
                  sx={{
                    bgcolor: COLORS.botBubble,
                    borderRadius: "18px 18px 18px 4px",
                    px: 2,
                    py: 1.5,
                    border: `1px solid ${COLORS.border}`,
                    display: "flex",
                    gap: 0.6,
                  }}
                >
                  {[0, 1, 2].map((i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        bgcolor: COLORS.brandLight,
                        animation: `${pulse} 1s ease-in-out infinite`,
                        animationDelay: `${i * 0.18}s`,
                      }}
                    />
                  ))}
                </Box>
              </Stack>
            </Fade>
          )}

          <div ref={bottomRef} />
        </Stack>

        {/* Recording banner */}
        {recording && (
          <Box
            sx={{
              px: 2,
              py: 0.75,
              bgcolor: "#FFEBEE",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: "#D32F2F",
                animation: `${pulse} 1s ease-in-out infinite`,
              }}
            />
            <Typography sx={{ fontSize: 13, color: "#C62828", fontWeight: 600 }}>
              Listening… tap stop when done
            </Typography>
          </Box>
        )}

        {/* Composer */}
        <Box
          sx={{
            px: 1.5,
            py: 1.5,
            background: "#fff",
            borderTop: `1px solid ${COLORS.border}`,
            flexShrink: 0,
            pb: "max(12px, env(safe-area-inset-bottom))",
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              bgcolor: "#F4F7F8",
              borderRadius: 3,
              border: `1px solid ${COLORS.border}`,
              px: 0.75,
              py: 0.35,
              transition: "border-color 0.2s, box-shadow 0.2s",
              "&:focus-within": {
                borderColor: COLORS.brandLight,
                boxShadow: "0 0 0 3px rgba(26, 35, 126, 0.12)",
              },
            }}
          >
            <TextField
              fullWidth
              multiline
              maxRows={3}
              placeholder="Ask about health or emergency…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendText();
                }
              }}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: {
                  fontSize: 14,
                  px: 1,
                  py: 0.75,
                  color: COLORS.brand,
                },
              }}
            />

            {!recording ? (
              <IconButton
                onClick={startRecording}
                disabled={loading}
                sx={{
                  color: COLORS.muted,
                  "&:hover": { color: COLORS.brand, bgcolor: COLORS.brandSoft },
                }}
              >
                <MicIcon />
              </IconButton>
            ) : (
              <IconButton
                onClick={stopRecording}
                sx={{
                  color: "#fff",
                  bgcolor: "#D32F2F",
                  animation: `${recordPulse} 1.4s ease-out infinite`,
                  "&:hover": { bgcolor: "#B71C1C" },
                }}
              >
                <StopIcon />
              </IconButton>
            )}

            <IconButton
              onClick={sendText}
              disabled={loading || !input.trim()}
              sx={{
                bgcolor: input.trim() && !loading ? COLORS.brand : "#D0D8DC",
                color: "#fff",
                width: 40,
                height: 40,
                transition: "background-color 0.2s, transform 0.15s",
                "&:hover": {
                  bgcolor: COLORS.brandMid,
                  transform: "scale(1.04)",
                },
                "&.Mui-disabled": {
                  bgcolor: "#D0D8DC",
                  color: "#fff",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={18} sx={{ color: "#fff" }} />
              ) : (
                <SendIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

export default AIChatScreen;
